import { useFormikContext } from 'formik'
import { Dispatch, SetStateAction, useState } from 'react'
import { ETranslationNs, ICaptchaVerify, IUserSignupForm } from 'src/types'
import { css } from '@emotion/css'
import { Box, Button, IconButton, InputAdornment, useMediaQuery, useTheme } from '@mui/material'
import {
  Container,
  DontHaveAccount,
  DontHaveAccountText,
  SecondaryAuthOptionsHolder,
  SubmitBtn,
  Texts,
  Title,
} from './SignUpFormV2.styled'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { useTranslation } from 'react-i18next'
import { EyeClosedIcon } from 'src/assets/icons/v2/EyeClosedIcon'
import { EyeOpenedIcon } from 'src/assets/icons/v2/EyeOpenedIcons'
import { ROUTES } from 'src/constants'
import { Link } from 'react-router-dom'
import { useTLoginTypes } from 'src/hooks/login-types/use-login-types.hook'
import {
  AppleIcon,
  GoogleIcon,
  LinkedinIcon,
  MicrosoftIcon,
  TrackgoodLogo,
  TwitterIcon,
} from 'src/assets/icons'
import { SecondaryAuth } from 'src/components/sign-in/v2/SecondaryAuth/SecondaryAuth'
import { OrBlock } from 'src/components/sign-in/v2/OrBlock/OrBlock'
import { Web3Provider } from 'src/components/auth-buttons/web3'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'
import { Loader } from 'src/components/loader'

interface ISignupInitialStepProps {
  setStep: Dispatch<SetStateAction<number>>
  captcha: ICaptchaVerify
}

interface IAuth {
  google: boolean
  apple: boolean
  microsoft: boolean
  linkedin: boolean
  x: boolean
  password: boolean
  web3: boolean
}

export const SignUpFormV2 = ({ setStep, captcha }: ISignupInitialStepProps) => {
  const { errors, values, handleChange, validateField } = useFormikContext<IUserSignupForm>()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const { data } = useTLoginTypes()
  const result: IAuth = {
    google: false,
    apple: false,
    microsoft: false,
    linkedin: false,
    x: false,
    password: false,
    web3: false,
  }

  data?.data.forEach((item) => {
    result[item.name as keyof IAuth] = item.active
  })
  const isOrShown: boolean =
    !!result.password &&
    (result.apple || result.google || result.linkedin || result.microsoft || result.x)

  const [t] = useTranslation()
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile_s'))

  const handleStartClick = () => {
    if (errors.email || errors.password) return
    if (!values.email || !values.password) {
      validateField('email')
      validateField('password')
      return
    }
    setStep(1)
  }

  if (!data) {
    return <Loader />
  }

  return (
    <div
      className={css(
        Container,
        `height: ${isTablet ? '90vh' : '100%'};`,
        isLaptop && `width: 500px; margin: auto;`,
        isTablet && `width: 100%;`,
        isMobile && `width: 100vw;`,
      )}
    >
      {isLaptop && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '40px',
          }}
        >
          <TrackgoodLogo />
        </div>
      )}
      <div className={css(Texts, isMobile && `padding: 0px; width: 90%; gap: 0px;`)}>
        <p className={css(Title, isMobile && `font-size: 20px;`)}>{t('register-title-text.v')}</p>
        {/* <p className={css(GreetingText, isMobile && `font-size: 12px; padding: 0px 10px;`)}>
          {t('register-welcome-text.v')}
        </p> */}
      </div>
      {isLaptop && (
        <p className={css(DontHaveAccount, isMobile && `font-size: 12px;`)}>
          <HelperWrapper t="sign-in-email-opposite-text" />
          <Link
            to={ROUTES.LOGIN}
            className={css(DontHaveAccountText, `color: ${theme.palette.custom.primary}`)}
          >
            {' '}
            <HelperWrapper t="login-link-text" />
          </Link>
        </p>
      )}
      <div className={SecondaryAuthOptionsHolder}>
        {result.google && <SecondaryAuth captcha={captcha} icon={<GoogleIcon />} auth={'google'} />}
        {result.linkedin && (
          <SecondaryAuth captcha={captcha} icon={<LinkedinIcon />} auth={'linkedin'} />
        )}
        {result.microsoft && (
          <SecondaryAuth captcha={captcha} icon={<MicrosoftIcon />} auth={'microsoft'} />
        )}
        {result.x && <SecondaryAuth captcha={captcha} icon={<TwitterIcon />} auth={'x'} />}
        {result.apple && (
          <SecondaryAuth captcha={captcha} mb={4} icon={<AppleIcon />} auth={'apple'} />
        )}
        {result.web3 && <Web3Provider />}
      </div>
      {isOrShown && <OrBlock />}
      {result.password && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <TextInputWithHelper
            autoComplete="on"
            fullWidth
            inputName="email"
            handleChange={handleChange}
            value={values.email}
            error={errors.email ? t(errors.email + '.v') : ''}
            inputPlaceholder={t('account-form-email-input-placeholder.v', {
              ns: ETranslationNs.Form,
            })}
            inputUpperLabel={
              <HelperWrapper t={'email-input-label'} ns={ETranslationNs.Common} isInputLable />
            }
          />
          <TextInputWithHelper
            fullWidth
            inputName="password"
            type={showPassword ? 'text' : 'password'}
            endIcon={
              <InputAdornment style={{ zIndex: 2 }} position="end">
                <IconButton
                  size="small"
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <EyeOpenedIcon size={24} /> : <EyeClosedIcon size={24} />}
                </IconButton>
              </InputAdornment>
            }
            handleChange={handleChange}
            value={values.password}
            error={errors.password ? t(errors.password + '.v') : ''}
            inputUpperLabel={
              <HelperWrapper
                t={'login-password-input-label'}
                ns={ETranslationNs.Common}
                isInputLable
              />
            }
          />
          <Button
            onClick={handleStartClick}
            disabled={!!(errors.email || errors.password || !values.email || !values.password)}
            type="button"
            variant="outlined"
            color="secondary"
            className={css(SubmitBtn, isMobile && `width: 90%;`)}
          >
            <HelperWrapper t="sign-up-first-step-button" />
          </Button>
        </Box>
      )}
    </div>
  )
}
